/**
 * PackingPage - Main packing station interface
 *
 * Follows CatcherPage and BinDashboardPage patterns:
 * - Container with animate-enter
 * - Page header with icon box pattern
 * - MetricCards with proper variants (3-column grid)
 * - Large touch targets for warehouse gloves
 * - ARIA live regions for accessibility
 *
 * Workflow:
 * 1. Scan bin QR or garment QR to load order
 * 2. Verify items by scanning or clicking
 * 3. Report issues for P&P items if needed
 * 4. Complete packing when all items verified
 * 5. Manual flow: Show completion overlay, user clicks Done to mark SHIPPED
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from '@/contexts/LocationContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MetricCard } from '@/features/production-orders/components/metrics/MetricCard';
import { QRScannerInput } from '@/features/catcher-station/components/QRScannerInput';
import { PackerPriorityBadge } from '@/features/bin-dashboard/components/PackerPriorityBadge';
import { ExpressBadge } from '@/features/bin-dashboard/components/ExpressBadge';
import { useBinOccupancy } from '@/features/bin-dashboard/hooks/useBinOccupancy';
import { PackingSlip } from './PackingSlip';
import { ItemChecklist, type PackingItemResult } from './ItemChecklist';
import { PackingPreviewThumbnails, buildPackingPreviewEntries } from './PackingPreviewThumbnails';
import {
  buildProductionLightboxData,
  productionPreviewKey,
  type ProductionPreviewKind,
} from '@/components/production/ProductionPreviewTile';
import { useProductionLightbox } from '@/components/production/useProductionLightbox';
import { IssueReportDialog, type IssueType } from './IssueReportDialog';
import { ReplacementDialog, type IssueReportResult } from './ReplacementDialog';
import { ProgressIndicator } from './ProgressIndicator';
import {
  useScanForPacking,
  useVerifyItem,
  useReportIssue,
} from '../hooks/usePackingMutation';
import { usePackingOrder } from '../hooks/usePackingOrder';
import { usePackingStats, useRecentlyPacked } from '../hooks/usePackingStats';
import { useShippingSettings, useMarkManualComplete } from '../hooks/useShipping';
import { ShippingPanel, type HandoverOutcome } from './ShippingPanel';
import { ManifestsSection } from './ManifestsSection';
import { AddToManifestDialog } from './AddToManifestDialog';
import { useCustomsManifestStatus } from '../hooks/useCustomsManifest';
import { PackingCompletionOverlay } from './PackingCompletionOverlay';
import { useScanFeedback } from '@/hooks/useScanFeedback';
import { useScannerAutoFocus } from '@/hooks/useScannerAutoFocus';
import { ScanErrorModal } from '@/components/ui/scan-error-modal';
import { SupersededStickerDialog } from '@/components/production/SupersededStickerDialog';
import { ProductionImageLightbox } from '@/components/production/ProductionImageLightbox';
import { toast } from 'sonner';
import { parseRedoError, parseStationError, parseOrderOnHold, parseSupersededError, getUserMessage, handoverRefusalCopyKey } from '@/lib/trpc-error-utils';
import { useBackendHeartbeat } from '@/hooks/useBackendHeartbeat';
import { ConnectionStatusBanner } from '@/components/ui/connection-status-banner';
import { extractGarmentId } from '@/lib/qr-utils';
import { useMyStation } from '../hooks/useMyStation';
import {
  Package,
  PackageOpen,
  Box,
  AlertCircle,
  Loader2,
  CheckCircle2,
  Clock,
  ArrowLeft,
  Store,
  FilePlus2,
  Images,
  HandHelping,
} from 'lucide-react';
import { useHotkeyEngine, matchesCombo } from '@/features/production-settings/hotkeys/useHotkeyEngine';
import { useHotkeySettings } from '@/features/production-settings/hotkeys/useHotkeySettings';
import { isLabelReturnProvider } from '@/features/production-settings/constants/shipping-providers';
import { formatOrderNumber, type HotkeyAction } from '@threadheads/shared';
import { formatBindingLabel } from '@/features/production-settings/hotkeys/hotkey-types';

function formatTimeAgo(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function PackingPage() {
  const { selectedLocation } = useLocation();
  const { t } = useTranslation('production');
  const heartbeat = useBackendHeartbeat();

  // Scan feedback (modal-based errors)
  const feedback = useScanFeedback();

  // Workflow state
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [issueDialogItem, setIssueDialogItem] = useState<PackingItemResult | null>(null);
  const [issueResult, setIssueResult] = useState<IssueReportResult | null>(null);
  const [showReplacementDialog, setShowReplacementDialog] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [verifyingItemId, setVerifyingItemId] = useState<string | null>(null);

  // Completion overlay state (manual shipping flow)
  const [showCompletionOverlay, setShowCompletionOverlay] = useState(false);
  const [pendingShipOrderId, setPendingShipOrderId] = useState<string | null>(null);
  const [pendingShipOrderName, setPendingShipOrderName] = useState<string | null>(null);
  const [pendingShipIsPickup, setPendingShipIsPickup] = useState(false);
  const [completionError, setCompletionError] = useState<string | null>(null);

  // Track whether LabelPrintDialog is open (reported by ShippingPanel)
  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  // Manual manifest recovery dialog. Declared with the other overlay state —
  // ABOVE the scanner/hotkey pause lists that read it and above the location
  // guard's early return (hooks must run in the same order on every render).
  const [addToManifestOpen, setAddToManifestOpen] = useState(false);
  const [manifestPdfImportOpen, setManifestPdfImportOpen] = useState(false);

  // Refs for focus management and scroll
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  // Synchronous guard to prevent overlapping scan processing
  const isScanningRef = useRef(false);
  // C-3: Synchronous guard to prevent double-click on Done button
  const isDismissingRef = useRef(false);

  // Queries
  const locationId = selectedLocation?.id ?? null;
  const { data: order, isLoading: orderLoading, error: orderError } = usePackingOrder(
    currentOrderId,
    locationId
  );
  const { data: stats } = usePackingStats(locationId);
  const { data: occupancy } = useBinOccupancy();
  const { data: recentlyPacked } = useRecentlyPacked(locationId);
  const {
    data: shippingSettings,
    isError: shippingSettingsErrored,
    refetch: refetchShippingSettings,
  } = useShippingSettings(locationId, currentOrderId);
  const { skipVerification } = useMyStation(locationId);

  // Mutations
  const scanMutation = useScanForPacking();
  const verifyMutation = useVerifyItem();
  const reportMutation = useReportIssue();
  const markManualCompleteMutation = useMarkManualComplete();

  // M-1: Full state reset on location change (prevents stale overlay/error state)
  useEffect(() => {
    setCurrentOrderId(null);
    setShowCompletionOverlay(false);
    setPendingShipOrderId(null);
    setPendingShipOrderName(null);
    setPendingShipIsPickup(false);
    setCompletionError(null);
    isDismissingRef.current = false;
  }, [selectedLocation?.id]);

  // Derived state
  const isBusy =
    scanMutation.isPending ||
    reportMutation.isPending;

  // Show loading toast while scan is processing
  const scanningToastRef = useRef<string | number | null>(null);
  useEffect(() => {
    if (scanMutation.isPending) {
      scanningToastRef.current = toast.loading('Scanning...');
    } else if (scanningToastRef.current !== null) {
      toast.dismiss(scanningToastRef.current);
      scanningToastRef.current = null;
    }
  }, [scanMutation.isPending]);

  // Order-wide lightbox: ONE FLAT LIST of every image on this order, in
  // checklist order, built from the SAME pure entry builder the thumbnails use
  // — so clicking any tile opens that exact image and the arrows then ride the
  // whole order. The overlay's state (the clicked tile's fallthrough, the
  // empty-list guard, the reset when the order goes) is
  // `useProductionLightbox`, shared with the other stations.
  const lightboxData = useMemo(
    () =>
      buildProductionLightboxData(
        (order?.items ?? []).map((item) => ({
          subjectId: item.garmentUnitId,
          subject: item.productName,
          entries: buildPackingPreviewEntries(item),
        })),
      ),
    [order?.items],
  );
  const lightbox = useProductionLightbox({
    entries: lightboxData.entries,
    indexByKey: lightboxData.indexByKey,
    resetKey: currentOrderId,
  });
  const openLightboxAt = lightbox.openAt;
  const setLightboxIndex = lightbox.setIndex;
  const setLightboxOpen = lightbox.setOpen;
  const hasOrderImages = lightboxData.entries.length > 0;

  // The whole order from the top: the View images button and the
  // packer_preview hotkey (BLU-1271). setIndex opens as well as moves.
  const openOrderImages = useCallback(() => setLightboxIndex(0), [setLightboxIndex]);

  const handleOpenPreview = useCallback(
    (garmentUnitId: string, kind: ProductionPreviewKind, sourceIndex: number) => {
      openLightboxAt(productionPreviewKey(garmentUnitId, kind), sourceIndex);
    },
    [openLightboxAt],
  );

  // USB scanners type characters as keyboard input - redirect to scanner field.
  // Every focus-trapped overlay pauses it (PRODUCTION_DESIGN §6.2) — otherwise the
  // idle QRScannerInput stays live behind the dialog and fights Radix's focus trap.
  useScannerAutoFocus({
    paused: !!issueDialogItem || showReplacementDialog || feedback.isModalOpen || showCompletionOverlay || printDialogOpen || addToManifestOpen || manifestPdfImportOpen || lightbox.open,
  });

  // Clear order and reset state
  const handleClearOrder = useCallback(() => {
    setCurrentOrderId(null);
    setStatusMessage(t('packer.orderCleared'));
  }, []);

  // Configurable hotkeys via useHotkeyEngine (BLU-289)
  const { hotkeyMap } = useHotkeySettings();
  const packingHotkeyHandlers = useMemo(() => {
    if (!currentOrderId) return {};
    return {
      packer_clear: () => handleClearOrder(),
      packer_ship: () => {
        const primaryBtn = document.querySelector<HTMLButtonElement>('[data-packing-primary]:not(:disabled)');
        if (primaryBtn) primaryBtn.click();
      },
      packer_manual: () => {
        const manualBtn = document.querySelector<HTMLButtonElement>('[data-packing-manual]:not(:disabled)');
        if (manualBtn) manualBtn.click();
      },
      packer_copy: () => {
        const copyBtn = document.querySelector<HTMLButtonElement>('[data-packing-copy]');
        if (copyBtn) copyBtn.click();
      },
      // Left out when the order has no images, so the engine does not
      // swallow the key for a no-op.
      ...(hasOrderImages ? { packer_preview: openOrderImages } : {}),
    } as Partial<Record<HotkeyAction, () => void>>;
  }, [currentOrderId, handleClearOrder, hasOrderImages, openOrderImages]);

  useHotkeyEngine(hotkeyMap, packingHotkeyHandlers, {
    paused: !!issueDialogItem || showReplacementDialog || feedback.isModalOpen || showCompletionOverlay || addToManifestOpen || manifestPdfImportOpen || lightbox.open,
  });

  // The same key closes the view again. The engine is paused while the
  // lightbox traps focus, so this half of the toggle lives here, on WINDOW
  // capture: that runs before the engine's document-capture listener, which
  // lets a held key's auto-repeat be swallowed whether the view is open or
  // shut. Without that, holding the key past the repeat delay flickers the
  // view shut and open again. Escape still closes through the Dialog, and
  // either way the lightbox hands focus back to the scanner (Radix runs
  // onCloseAutoFocus on unmount, whatever closed it).
  const previewBinding = hotkeyMap.packer_preview;
  const lightboxOpen = lightbox.open;
  useEffect(() => {
    if (!hasOrderImages || previewBinding?.type !== 'combo') return;
    const handlePreviewToggle = (e: KeyboardEvent) => {
      if (!matchesCombo(e, previewBinding)) return;
      if (e.repeat) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // Shut: the engine opens it.
      if (!lightboxOpen) return;
      e.preventDefault();
      e.stopPropagation();
      setLightboxOpen(false);
    };
    window.addEventListener('keydown', handlePreviewToggle, true);
    return () => window.removeEventListener('keydown', handlePreviewToggle, true);
  }, [hasOrderImages, previewBinding, lightboxOpen, setLightboxOpen]);

  // Shared error handler for scan mutations (station mismatch, redo blocked, generic)
  const handleScanError = useCallback((error: unknown) => {
    // Part 3: an ON_HOLD order block takes precedence (amber, set aside). The existing
    // ROUTED_AWAY/CANCELLED garment case has no dedicated branch here and falls to the generic
    // error below — left as-is (pre-existing); ON_HOLD gets its purpose-built modal.
    const superseded = parseSupersededError(error);
    if (superseded.isSuperseded) {
      // Sticker invalidated by a re-batch — shirt is fine; offer the new sticker (amber).
      feedback.triggerSuperseded(superseded.replacement);
      return;
    }
    const onHold = parseOrderOnHold(error);
    if (onHold.isOnHold) {
      feedback.triggerOrderOnHold(onHold.holdReason);
      return;
    }
    const stationErr = parseStationError(error);
    if (stationErr.isStationError) {
      if (stationErr.code === 'STATION_MISMATCH') {
        feedback.triggerWarning(
          t('packer.wrongStation'),
          getUserMessage(error),
          stationErr.correctStation ? `This order should go to: ${stationErr.correctStation}` : undefined
        );
      } else {
        feedback.triggerError(t('packer.notAssigned'), getUserMessage(error));
      }
    } else {
      const parsed = parseRedoError(error);
      if (parsed.isRedoError) {
        feedback.triggerRedoBlocked(parsed.replacement);
      } else if ('isOrderItemRemoved' in parsed && parsed.isOrderItemRemoved) {
        feedback.triggerOrderItemRemoved();
      } else {
        feedback.triggerError(t('scanError'), getUserMessage(error));
      }
    }
  }, [feedback, t]);

  // Handle scan - could be bin QR or garment QR
  const handleScan = useCallback(
    async (qrCode: string) => {
      if (!locationId || isScanningRef.current) return;
      isScanningRef.current = true;

      try {
        // If we have an order loaded, try to verify item first
        if (currentOrderId) {
          // Try to extract garment ID using the qr-utils (supports V1 and legacy formats)
          const garmentId = extractGarmentId(qrCode);
          if (garmentId) {
            // BLU-286: If all items already verified (shipping phase), handle scan
            // as order-switch or "already verified" toast instead of calling verifyMutation.
            const allVerified = order && (order.verifiedCount >= order.totalItems && order.totalItems > 0);
            if (allVerified || skipVerification) {
              const belongsToCurrentOrder = order?.items.some(
                (item) => item.garmentUnitId === garmentId
              );
              if (belongsToCurrentOrder) {
                toast.info(t('packer.alreadyVerified'));
                return;
              }
              // BLU-282: Block order switching in manual shipping flow (UX guard only).
              // When the resolved provider isn't a label-return provider
              // (registry-driven), packer must click "Mark Complete" first.
              const canUseLabelProvider = isLabelReturnProvider(shippingSettings?.shippingProvider)
                && (shippingSettings?.hasCredentials ?? false);
              if (shippingSettings !== undefined && !canUseLabelProvider) {
                feedback.triggerWarning(
                  t('packer.completeOrderFirst'),
                  t('packer.completeOrderFirstDesc')
                );
                return;
              }

              // Different order — load via scanMutation.
              // Note: FAILED garments throw REDO_GARMENT on the backend and land
              // in handleScanError (redo-blocked modal), never reaching here.
              try {
                const result = await scanMutation.mutateAsync({
                  qrCode,
                  locationId,
                });
                // BLU-286: If backend returns same order (race with cache refresh),
                // show informative toast instead of silently reloading
                if (result.orderId === currentOrderId) {
                  toast.info(t('packer.alreadyVerified'));
                } else {
                  setCurrentOrderId(result.orderId);
                  setStatusMessage(t('packer.orderLoaded', { orderName: result.orderName }));
                }
              } catch (error) {
                handleScanError(error);
              }
              return;
            }

            // Normal verification flow (items still being verified)
            try {
              const result = await verifyMutation.mutateAsync({
                garmentUnitId: garmentId,
                orderId: currentOrderId,
                locationId,
              });
              if (result.allVerified) {
                setStatusMessage(t('packer.allVerifiedReady'));
              } else {
                setStatusMessage(t('packer.itemVerifiedRemaining', { remaining: result.remainingItems }));
              }
              scrollAreaRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
              return;
            } catch (error) {
              const onHold = parseOrderOnHold(error);
              const parsed = parseRedoError(error);
              const superseded = parseSupersededError(error);
              if (superseded.isSuperseded) {
                feedback.triggerSuperseded(superseded.replacement);
              } else if (onHold.isOnHold) {
                feedback.triggerOrderOnHold(onHold.holdReason);
              } else if (parsed.isRedoError) {
                feedback.triggerRedoBlocked(parsed.replacement);
              } else if ('isOrderItemRemoved' in parsed && parsed.isOrderItemRemoved) {
                feedback.triggerOrderItemRemoved();
              } else {
                feedback.triggerError(t('packer.verificationError'), getUserMessage(error));
              }
            }
            return;
          }

          // Fall through to try as order/bin scan if not a garment QR
          // (e.g., user scanned a bin QR while order is loaded)
        }

        // Scan for order (bin QR or initial garment scan)
        try {
          const result = await scanMutation.mutateAsync({
            qrCode,
            locationId,
          });
          setCurrentOrderId(result.orderId);
          setStatusMessage(t('packer.orderLoaded', { orderName: result.orderName }));
        } catch (error) {
          handleScanError(error);
        }
      } finally {
        isScanningRef.current = false;
      }
    },
    [currentOrderId, locationId, order, skipVerification, shippingSettings, scanMutation, verifyMutation, feedback, handleScanError]
  );

  // Handle item verification via click
  const handleVerifyItem = useCallback(
    async (garmentUnitId: string) => {
      if (!currentOrderId || !locationId) return;
      setVerifyingItemId(garmentUnitId);

      try {
        const result = await verifyMutation.mutateAsync({
          garmentUnitId,
          orderId: currentOrderId,
          locationId,
        });
        if (result.allVerified) {
          setStatusMessage(t('packer.allVerifiedReady'));
        } else {
          setStatusMessage(t('packer.itemVerifiedRemaining', { remaining: result.remainingItems }));
        }
        scrollAreaRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (error) {
        const onHold = parseOrderOnHold(error);
        const parsed = parseRedoError(error);
        const superseded = parseSupersededError(error);
        if (superseded.isSuperseded) {
          feedback.triggerSuperseded(superseded.replacement);
        } else if (onHold.isOnHold) {
          feedback.triggerOrderOnHold(onHold.holdReason);
        } else if (parsed.isRedoError) {
          feedback.triggerRedoBlocked(parsed.replacement);
        } else {
          feedback.triggerError(t('packer.verificationError'), getUserMessage(error));
        }
      } finally {
        setVerifyingItemId(null);
      }
    },
    [currentOrderId, locationId, verifyMutation, feedback]
  );

  // Handle issue report dialog open
  const handleReportIssue = useCallback((item: PackingItemResult) => {
    setIssueDialogItem(item);
  }, []);

  // Handle issue report submission
  const handleSubmitIssue = useCallback(
    async (issueType: IssueType, notes?: string) => {
      if (!issueDialogItem || !currentOrderId || !locationId) return;

      try {
        const result = await reportMutation.mutateAsync({
          garmentUnitId: issueDialogItem.garmentUnitId,
          orderId: currentOrderId,
          locationId,
          issueType,
          notes,
        });
        setIssueDialogItem(null);
        setIssueResult(result as IssueReportResult);
        setShowReplacementDialog(true);
        setStatusMessage(t('packer.issueReported'));
      } catch (error) {
        feedback.triggerError(t('packer.issueSubmitFailed'), getUserMessage(error));
      }
    },
    [issueDialogItem, currentOrderId, locationId, reportMutation, feedback]
  );

  // Handle replacement dialog close
  const handleReplacementContinue = useCallback(() => {
    setShowReplacementDialog(false);
    setIssueResult(null);
  }, []);

  // A Hand over order left this station (ShippingPanel's hand over branch).
  // No completion overlay: the order is either packed and on its way to the
  // hand over bench, or handed over to the client. Clear and say which.
  const handleHandover = useCallback((outcome: HandoverOutcome) => {
    const orderName = order?.orderName ?? '';
    const message =
      outcome === 'sent_to_bench'
        ? t('packer.handoverSentToBench', { orderName })
        : outcome === 'already_handed_over'
          ? t('packer.handoverAlreadyDone', { orderName })
          : t('packer.handoverDone', { orderName });
    setCurrentOrderId(null);
    setStatusMessage(message);
    if (outcome === 'already_handed_over') toast.info(message);
    else toast.success(message);
  }, [order?.orderName, t]);

  // Handle shipment completion (from ShippingPanel)
  // manual=true: order is PACKED only, show overlay for user to confirm SHIPPED
  // manual=false: StarShipIt already confirmed shipment, clear state immediately
  const handleShipmentComplete = useCallback(({ manual }: { manual: boolean }) => {
    if (manual) {
      if (order?.isPickup) {
        // Pickup: skip overlay, call markManualComplete directly
        if (!currentOrderId || !locationId) return;
        if (isDismissingRef.current) return; // BLU-338: Prevent double-tap (matches overlay path)
        isDismissingRef.current = true;
        markManualCompleteMutation.mutateAsync({
          orderId: currentOrderId,
          locationId,
        }).then((result) => {
          isDismissingRef.current = false;
          setCurrentOrderId(null);
          if (result.alreadyExternallyFulfilled) {
            // Race: order became EXTERNALLY_FULFILLED after load — see
            // handleCompletionDone for the same C-5/H-5 distinction.
            toast.error(t('shipping.orderExternallyFulfilled'));
            setStatusMessage(t('packer.alreadyExternallyFulfilled'));
          } else {
            setStatusMessage(t('packer.orderReadyPickup'));
          }
        }).catch((error) => {
          isDismissingRef.current = false;
          // BLU-338: Show visible error toast (previously only set sr-only statusMessage)
          // Hand over refusals arrive as a message marker: show plain copy.
          const refusalKey = handoverRefusalCopyKey(error);
          toast.error(refusalKey ? t(refusalKey) : getUserMessage(error));
        });
      } else {
        // Shipping: show overlay (existing behavior)
        isDismissingRef.current = false;
        setPendingShipOrderId(currentOrderId);
        setPendingShipOrderName(order?.orderName ?? null);
        setPendingShipIsPickup(false);
        setCompletionError(null);
        setShowCompletionOverlay(true);
      }
    } else {
      // StarShipIt path — already SHIPPED, clear state
      setCurrentOrderId(null);
      setStatusMessage(order?.isPickup ? t('packer.orderReadyPickup') : t('packer.orderShipped'));
    }
  }, [currentOrderId, order?.orderName, order?.isPickup, locationId, markManualCompleteMutation, t]);

  // Handle "Done" button on completion overlay — marks order as SHIPPED
  // C-3: Uses isDismissingRef to prevent double-click race condition
  const handleCompletionDone = useCallback(async () => {
    if (!pendingShipOrderId || !locationId) return;
    if (isDismissingRef.current) return; // Prevent double-click

    isDismissingRef.current = true;
    setCompletionError(null);

    // BLU-338: Track whether mutation completed so timeout doesn't fire after success/error
    let mutationCompleted = false;

    // Safety timeout — reset isDismissingRef if mutation hangs (network timeout)
    // Without this, a hung request permanently blocks the "Done" button
    const safetyTimeout = setTimeout(() => {
      if (!mutationCompleted && isDismissingRef.current) {
        isDismissingRef.current = false;
        setCompletionError('Request timed out. Please try again.');
      }
    }, 15_000);

    try {
      const result = await markManualCompleteMutation.mutateAsync({
        orderId: pendingShipOrderId,
        locationId,
      });
      mutationCompleted = true;
      clearTimeout(safetyTimeout);
      // Success — dismiss overlay, reset state
      setShowCompletionOverlay(false);
      setPendingShipOrderId(null);
      setPendingShipOrderName(null);
      const wasPickup = pendingShipIsPickup;
      setPendingShipIsPickup(false);

      setCurrentOrderId(null);
      if (result.alreadyExternallyFulfilled) {
        // Race: order became EXTERNALLY_FULFILLED between load and "Done" —
        // H-5 stays idempotent-success (never throws), so show the same
        // "fulfilled outside the app" notice instead of a shipped toast.
        toast.error(t('shipping.orderExternallyFulfilled'));
        setStatusMessage(t('packer.alreadyExternallyFulfilled'));
      } else {
        setStatusMessage(wasPickup ? t('packer.orderReadyPickup') : t('packer.orderShipped'));
      }
    } catch (error) {
      mutationCompleted = true;
      clearTimeout(safetyTimeout);
      // Show error on overlay — user can retry
      isDismissingRef.current = false;
      const refusalKey = handoverRefusalCopyKey(error);
      setCompletionError(
        refusalKey ? t(refusalKey) : error instanceof Error && error.message ? error.message : 'Failed to complete shipment'
      );
    }
  }, [pendingShipOrderId, locationId, markManualCompleteMutation, t]);

  // Handle "Undo" on completion overlay — dismiss overlay, return to packing view
  // Order stays PACKED (safe state), user can re-ship or continue verifying
  const handleCompletionUndo = useCallback(() => {
    setShowCompletionOverlay(false);
    setPendingShipOrderId(null);
    setPendingShipOrderName(null);
    setPendingShipIsPickup(false);
    setCompletionError(null);
    isDismissingRef.current = false;
  }, []);

  // Completion overlay hotkeys: Enter = Done, Backspace = Undo
  useEffect(() => {
    if (!showCompletionOverlay) return;

    const handleOverlayHotkey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        handleCompletionDone();
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        e.stopPropagation();
        handleCompletionUndo();
      }
    };

    document.addEventListener('keydown', handleOverlayHotkey, true);
    return () => document.removeEventListener('keydown', handleOverlayHotkey, true);
  }, [showCompletionOverlay, handleCompletionDone, handleCompletionUndo]);

  // Update status message for screen readers
  useEffect(() => {
    if (orderLoading) {
      setStatusMessage(t('loadingOrder'));
    }
  }, [orderLoading]);

  // Reuses the customs-manifest status query that already drives ManifestsSection;
  // `applicable` is true only where this location declares via Portless, so the
  // Add To Manifest button never appears on an HQ/StarShipIt floor.
  const { data: customsStatus } = useCustomsManifestStatus(locationId ?? null);

  // Location guard
  if (!selectedLocation) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-7xl animate-enter">
        <Card>
          <CardContent className="py-16 text-center">
            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" aria-hidden="true" />
            <p className="text-lg text-muted-foreground">
              {t('packer.selectLocation')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isActive = !!currentOrderId;
  const isPortlessManifestLocation = !!customsStatus?.applicable;
  // Derived: order is in shipping phase (all items verified or skip-verification mode)
  const isShippingPhase = !!order && (
    skipVerification || (order.verifiedCount >= order.totalItems && order.totalItems > 0)
  );
  // BLU per-client shipping: shippingSettings now resolves per current order
  // (its effective provider can differ order-to-order at a 3PL shared
  // location). While switching orders the query key changes and
  // `shippingSettings` goes undefined for a beat — that's "unresolved for
  // this order", never coalesce it to a default provider. Coalescing to
  // 'manual' would render an actionable Mark Complete button before we
  // actually know the order's provider — a mis-complete hazard.
  // Loading and error both leave `data` undefined — split them so a failed
  // query surfaces a Retry instead of an infinite spinner (soft-lock).
  const shippingSettingsError = !!currentOrderId && shippingSettings === undefined && shippingSettingsErrored;
  const shippingSettingsLoading = !!currentOrderId && shippingSettings === undefined && !shippingSettingsErrored;
  // BLU-695 — a top-up unit still in production; drives the "still in
  // production" banner below. Counted once, used for both the gate and the copy.
  const notYetMadeCount = order?.items.filter((i) => i.notYetMade).length ?? 0;
  // BLU-282: Determines scanner card style and whether order switching is allowed.
  // Phase 51: true for any label-return provider (registry-driven).
  const canUseLabelProviderRender = !shippingSettingsLoading
    && isLabelReturnProvider(shippingSettings?.shippingProvider)
    && (shippingSettings?.hasCredentials ?? false);
  const oldestReadyBin = occupancy?.oldestReadyBin ?? null;
  // Hand over order, packed and not yet handed over: waiting at the hand over
  // bench for the client. Any packer at any station can hand it over.
  const packedForHandover =
    shippingSettings?.shippingProvider === 'handover'
    && !!order?.isAlreadyPacked
    && !order?.isShipped;

  // Portless cross-dock label readiness (informational — never blocks; PR3 gates).
  // Only meaningful for the Portless provider; hidden for every other provider.
  const isPortlessProvider = shippingSettings?.shippingProvider === 'portless';
  const isPortlessLabelReady = (order?.portlessLabelReadyAt ?? null) !== null;
  // A react-query cache written by the previous deploy has no packages field.
  const manualPackages = order?.packages ?? [];
  // Empty when unbound: the View images button then drops its key badge.
  const previewBindingLabel = formatBindingLabel(hotkeyMap.packer_preview);

  return (
    <div className="container mx-auto px-6 py-6 max-w-7xl space-y-6 animate-enter">
      {/* ARIA live region for screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {statusMessage}
      </div>

      {/* Page header - contextual based on state */}
      <div className="flex items-center gap-3">
        {isActive ? (
          <>
            <Button
              variant="ghost"
              onClick={handleClearOrder}
              className="shrink-0 gap-1.5"
              aria-label={t('backTo', { station: t('packer.title') }) + ` (${formatBindingLabel(hotkeyMap.packer_clear)})`}
            >
              <ArrowLeft className="h-5 w-5" />
              <kbd className="text-[10px] font-normal text-muted-foreground border bg-muted px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.packer_clear)}</kbd>
            </Button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold truncate">
                  {order?.orderName ?? t('loadingOrder')}
                </h1>
                {order?.isPickup && (
                  <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 shrink-0">
                    <Store className="h-3 w-3 mr-1" />
                    {t('packer.pickup')}
                  </Badge>
                )}
                {/* Portless cross-dock label readiness — informational only (PR3 gates). */}
                {order && isPortlessProvider && (
                  isPortlessLabelReady ? (
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 shrink-0">
                      <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                      {t('packer.labelReady')}
                    </Badge>
                  ) : (
                    // Outlined amber so it reads distinctly from the (fill-amber)
                    // pickup badge when both show at once — glanceable at distance.
                    <Badge className="bg-amber-50 text-amber-800 border border-amber-300 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-700 shrink-0">
                      <Clock className="h-3 w-3" aria-hidden="true" />
                      {t('packer.waitingForLabel')}
                    </Badge>
                  )
                )}
              </div>
              <p className="text-muted-foreground truncate">
                {order?.isPickup && order?.customerName
                  ? `Pickup order for ${order.customerName}`
                  : order?.customerName
                    ? `Verify and pack for ${order.customerName}`
                    : 'Verify items and complete packing'}
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="p-2 rounded-lg bg-primary/10">
              <PackageOpen className="h-6 w-6 text-primary" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">{t('packer.title')}</h1>
              <p className="text-muted-foreground">
                {t('packer.description')}{selectedLocation ? ` at ${selectedLocation.name}` : ''}
              </p>
            </div>
            {/* Manual manifest recovery. Right-aligned so it never competes with
                the scan-first flow — same placement as the Print station's
                "Test a Print File". Idle-only by construction (this whole arm is
                the no-order-loaded branch — there is no order in hand to recover
                mid-pack), and only at a location that actually declares via
                Portless, which `applicable` already answers. */}
            {isPortlessManifestLocation && (
              <div className="ml-auto flex shrink-0">
                <Button
                  variant="outline"
                  className="shrink-0 gap-2 h-12 px-4"
                  onClick={() => setAddToManifestOpen(true)}
                  aria-label={t('packer.addToManifest.button')}
                >
                  <FilePlus2 className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">{t('packer.addToManifest.button')}</span>
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <ConnectionStatusBanner heartbeat={heartbeat} />

      {/* Idle-only: Scanner input */}
      {!isActive && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t('scanBinOrOrder')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <QRScannerInput
              onScan={handleScan}
              disabled={isBusy}
              isLoading={scanMutation.isPending || verifyMutation.isPending}
              placeholder={t('scanBinOrOrderPlaceholder')}
            />
          </CardContent>
        </Card>
      )}

      {/* Idle-only: Packer priority badge */}
      {!isActive && occupancy?.packerPriority && (
        <PackerPriorityBadge
          priority={occupancy.packerPriority}
          occupancyPercent={occupancy.occupancyPercent}
          className="w-full"
        />
      )}

      {/* Active-only: Scanner input for item verification (above items list) */}
      {isActive && order && !skipVerification && order.verifiedCount < order.totalItems && (
        <Card>
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-base">{t('scanItemToVerify')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-3">
            <QRScannerInput
              onScan={handleScan}
              disabled={isBusy}
              isLoading={scanMutation.isPending || verifyMutation.isPending}
              placeholder={t('scanItemPlaceholder')}
            />
          </CardContent>
        </Card>
      )}

      {/* BLU-286: Scanner input during shipping phase (all items verified).
          Captures USB scanner keystrokes that would otherwise activate focused
          buttons (Print Label, Mark as Shipped) via native Enter activation. */}
      {isActive && order && !showCompletionOverlay && isShippingPhase && (
        <Card className={canUseLabelProviderRender ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-amber-500'}>
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-base">
              {canUseLabelProviderRender ? t('packer.scanNextOrder') : t('packer.completeOrderToContinue')}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-3">
            <QRScannerInput
              onScan={handleScan}
              disabled={isBusy}
              isLoading={scanMutation.isPending}
              placeholder={t('packer.scanNextOrderPlaceholder')}
            />
          </CardContent>
        </Card>
      )}

      {/* Idle-only: Next bin to pack */}
      {!isActive && (
        <Card className={oldestReadyBin ? 'border-l-4 border-l-blue-500' : ''}>
          <CardContent className="py-4">
            {oldestReadyBin ? (
              <div className="flex items-center justify-center gap-4">
                <div className="text-center">
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{t('packer.nextBin')}</p>
                  <p className="text-3xl font-bold">{oldestReadyBin.label}</p>
                  {oldestReadyBin.isExpress && <ExpressBadge className="mt-1" />}
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-center">{t('packer.noBinsReady')}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Idle-only: Recently packed list */}
      {!isActive && recentlyPacked?.orders && recentlyPacked.orders.length > 0 && (
        <Card>
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <CardTitle className="text-sm font-medium text-muted-foreground">{t('recentlyPacked')}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-2 pt-0">
            <div className="divide-y">
              {recentlyPacked.orders.map((order) => (
                <button
                  key={order.id}
                  className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left"
                  onClick={() => setCurrentOrderId(order.id)}
                >
                  <span className="font-medium text-sm min-w-0 shrink-0">{formatOrderNumber(order.orderName)}</span>
                  <span className="text-sm text-muted-foreground truncate">
                    {order.customerName ?? 'Unknown'}
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0 ml-auto">
                    {t('itemCount', { count: order.lineItemCount })}
                  </span>
                  {'isPickup' in order && order.isPickup && (
                    <Badge className="text-xs shrink-0 bg-amber-100 text-amber-800 hover:bg-amber-100">
                      {t('packer.pickup')}
                    </Badge>
                  )}
                  <Badge
                    variant={order.productionStatus === 'SHIPPED' || order.productionStatus === 'READY_FOR_PICKUP' ? 'default' : 'secondary'}
                    className={`text-xs shrink-0 ${order.productionStatus === 'SHIPPED' || order.productionStatus === 'READY_FOR_PICKUP' ? 'bg-green-100 text-green-800 hover:bg-green-100' : ''}`}
                  >
                    {order.productionStatus === 'READY_FOR_PICKUP' ? 'Pickup' : order.productionStatus === 'SHIPPED' ? 'Shipped' : 'Packed'}
                  </Badge>
                  <span className="text-xs text-muted-foreground shrink-0 w-14 text-right">
                    {formatTimeAgo(order.packedAt)}
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Idle-only: Stats row - 3 columns */}
      {!isActive && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            variant="production"
            title={t('packer.packedToday')}
            value={String(stats?.packedToday ?? 0)}
            icon={Package}
          />
          <MetricCard
            variant="info"
            title={t('packer.waitingToPack')}
            value={String(occupancy?.occupiedBins ?? 0)}
            subtitle={occupancy ? `${t('common:of')} ${occupancy.totalBins} bins` : undefined}
            icon={Package}
          />
          <MetricCard
            variant="total"
            title={t('packer.binsCleared')}
            value={String(stats?.binsClearedToday ?? 0)}
            icon={Box}
          />
        </div>
      )}

      {/* Idle-only: Customs manifests (Portless location only — self-gated) */}
      {!isActive && locationId && <ManifestsSection locationId={locationId} />}

      <AddToManifestDialog
        open={addToManifestOpen}
        onOpenChange={setAddToManifestOpen}
        locationId={locationId ?? null}
        onPdfOpenChange={setManifestPdfImportOpen}
      />

      {/* Error display */}
      {orderError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{orderError.message}</span>
            <Button variant="outline" size="sm" onClick={handleClearOrder}>
              {t('packer.clear')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Active packing layout */}
      {orderLoading ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Loader2 className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-spin" />
            <p className="text-lg text-muted-foreground">{t('loadingOrder')}</p>
          </CardContent>
        </Card>
      ) : order ? (
        <div className="flex flex-col min-h-[calc(100dvh-12rem)]">
          {/* Scrollable content area: progress, items, order details, scanner */}
          <div ref={scrollAreaRef} className="flex-1 overflow-y-auto space-y-6">
            {/* Progress indicator (moved to top) */}
            <ProgressIndicator
              verified={order.verifiedCount}
              total={order.totalItems}
              failedItemCount={order.failedItemCount}
            />

            {/* Already fulfilled outside the app (Shopify sync cascade) — hard
                block, checked before our own already-packed/shipped state */}
            {order.isExternallyFulfilled && (
              <Alert variant="destructive" className="[&>svg+div]:translate-y-0">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {t('packer.alreadyExternallyFulfilled')}
                </AlertDescription>
              </Alert>
            )}

            {/* A packed Hand over order is the normal waiting case (packed
                and sent to the bench until the client collects), so it is
                neutral info, not the red already-packed warning. */}
            {!order.isExternallyFulfilled && packedForHandover && (
              <Alert className="border-teal-300 bg-teal-50 text-teal-900 dark:border-teal-700 dark:bg-teal-950/30 dark:text-teal-100 [&>svg+div]:translate-y-0 [&>svg]:text-teal-600">
                <HandHelping className="h-4 w-4" aria-hidden="true" />
                <AlertDescription>{t('packer.handoverPackedWaiting')}</AlertDescription>
              </Alert>
            )}

            {/* Already packed/shipped warning */}
            {!order.isExternallyFulfilled && order.isAlreadyPacked && !packedForHandover && (
              <Alert variant="destructive" className="[&>svg+div]:translate-y-0">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {order.isShipped
                    ? order.isPickup
                      ? t('packer.alreadyReadyPickup')
                      : t('packer.alreadyShipped')
                    : t('packer.alreadyPacked')}
                </AlertDescription>
              </Alert>
            )}

            {/* Redo pending — blocks packing until all items arrive */}
            {order.hasRedoPending && !order.isAlreadyPacked && !order.isExternallyFulfilled && (
              <Alert variant="destructive" className="[&>svg+div]:translate-y-0">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {t('packer.redoInProgress')}
                </AlertDescription>
              </Alert>
            )}

            {/* BLU-695 — a top-up unit is still in production. Not a hard
                blocker like hasRedoPending above; tells the packer why the
                checklist won't complete yet. */}
            {notYetMadeCount > 0 && (
              <Alert variant="warning" className="[&>svg+div]:translate-y-0">
                <Clock className="h-4 w-4" />
                <AlertDescription>
                  {t('packer.itemsStillInProduction', { count: notYetMadeCount })}
                </AlertDescription>
              </Alert>
            )}

            {/* Item checklist — compact for auto-verified single-line orders */}
            {order.isSingleLine && order.verifiedCount === order.totalItems && order.totalItems > 0 ? (
              <Card className="border-green-200 bg-green-50/50">
                <CardContent className="py-5 px-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" aria-hidden="true" />
                      {order.items[0] && (
                        <PackingPreviewThumbnails
                          item={order.items[0]}
                          productName={order.items[0].productName}
                          onOpenPreview={(kind, sourceIndex) =>
                            handleOpenPreview(order.items[0]!.garmentUnitId, kind, sourceIndex)
                          }
                        />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium truncate">{order.items[0]?.productName}</p>
                        <p className="text-sm text-muted-foreground font-mono">{order.items[0]?.sku}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearOrder}
                      disabled={isBusy}
                    >
                      {t('packer.clear')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{t('packer.itemsToVerify')}</CardTitle>
                    <div className="flex items-center gap-2">
                      {hasOrderImages && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={openOrderImages}
                          aria-label={
                            previewBindingLabel
                              ? `${t('packer.preview.viewAll')} (${previewBindingLabel})`
                              : undefined
                          }
                        >
                          <Images aria-hidden="true" />
                          {t('packer.preview.viewAll')}
                          {previewBindingLabel && (
                            <kbd className="text-[10px] font-normal text-muted-foreground border bg-muted px-1.5 py-0.5 rounded">{previewBindingLabel}</kbd>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleClearOrder}
                        disabled={isBusy}
                      >
                        {t('packer.clearOrder')}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 px-0">
                  <ItemChecklist
                    items={order.items}
                    onVerify={handleVerifyItem}
                    onReportIssue={handleReportIssue}
                    onOpenPreview={handleOpenPreview}
                    isVerifyingId={verifyingItemId}
                  />
                </CardContent>
              </Card>
            )}

            {manualPackages.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">
                    {t('packer.packages.title', { count: manualPackages.length })}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {manualPackages.map((pkg) => (
                    <div key={pkg.id} className="rounded-md border p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono break-all">{pkg.trackingNumber}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {t(`packer.packages.contents.${pkg.contentsState}`)}
                        </Badge>
                        {pkg.carrier && <span className="text-sm text-muted-foreground">{pkg.carrier}</span>}
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {pkg.extractedSkus.length > 0
                          ? pkg.extractedSkus.map((sku) => `${sku.sku}${sku.quantity ? ` ×${sku.quantity}` : ''}`).join(', ')
                          : t('packer.packages.contentsUnknown')}
                        {pkg.internalReference && (
                          <><span aria-hidden="true"> · </span>{t('packer.packages.internalRef')}: {pkg.internalReference}</>
                        )}
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Packing slip (order details) */}
            <PackingSlip order={order} />
          </div>

          {/* Sticky bottom: Shipping Panel only */}
          <div className="sticky bottom-0 z-10 bg-background pt-2 pb-4 border-t space-y-2">
            {shippingSettingsError ? (
              // Query failed (retries exhausted): surface it with a Retry —
              // never fall through to a coalesced-'manual' ShippingPanel, and
              // never strand the packer on a dead spinner.
              <div
                className="flex min-h-[124px] w-full flex-col items-center justify-center gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-4"
                role="alert"
              >
                <span className="text-base font-medium text-destructive">
                  Couldn&rsquo;t load shipping for this order
                </span>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-12 px-6"
                  onClick={() => void refetchShippingSettings()}
                >
                  Retry
                </Button>
              </div>
            ) : shippingSettingsLoading ? (
              // BLU per-client shipping: this order's effective provider hasn't
              // resolved yet — never render ShippingPanel with a coalesced
              // 'manual' default here (see shippingSettingsLoading above).
              // min-h matches the resolved panel's ready state (primary +
              // secondary action) so the sticky footer doesn't jump on resolve.
              <div
                className="flex min-h-[124px] w-full items-center justify-center gap-2 rounded-md border bg-muted/30 text-muted-foreground"
                role="status"
                aria-live="polite"
              >
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                <span className="text-base font-medium">Loading shipping…</span>
              </div>
            ) : (
              <ShippingPanel
                orderId={currentOrderId!}
                locationId={locationId!}
                orderName={order.orderName}
                // shippingSettingsLoading === false here guarantees shippingSettings
                // is resolved for this order; the `?? ` fallbacks are unreachable,
                // kept only so the type checker doesn't need cross-variable narrowing.
                shippingProvider={shippingSettings?.shippingProvider ?? 'manual'}
                hasCredentials={shippingSettings?.hasCredentials ?? false}
                allItemsVerified={isShippingPhase}
                isPickup={order.isPickup}
                isExternallyFulfilled={order.isExternallyFulfilled}
                isCompletingManual={markManualCompleteMutation.isPending}
                onShipmentComplete={handleShipmentComplete}
                onPrintDialogChange={setPrintDialogOpen}
                onOrderOnHold={feedback.triggerOrderOnHold}
                isAlreadyPacked={order.isAlreadyPacked}
                onHandover={handleHandover}
              />
            )}
          </div>
        </div>
      ) : null}

      {/* Issue report dialog */}
      <IssueReportDialog
        open={!!issueDialogItem}
        onOpenChange={(open) => !open && setIssueDialogItem(null)}
        item={issueDialogItem}
        onSubmit={handleSubmitIssue}
        isPending={reportMutation.isPending}
      />

      {/* Replacement dialog */}
      <ReplacementDialog
        open={showReplacementDialog}
        onOpenChange={setShowReplacementDialog}
        issueResult={issueResult}
        item={issueDialogItem}
        onContinue={handleReplacementContinue}
      />

      {/* Completion overlay for manual shipping flow */}
      <PackingCompletionOverlay
        open={showCompletionOverlay}
        onDone={handleCompletionDone}
        onUndo={handleCompletionUndo}
        isSubmitting={markManualCompleteMutation.isPending}
        error={completionError}
        orderName={pendingShipOrderName ?? undefined}
        starshipitEnabled={shippingSettings?.shippingProvider === 'starshipit'}
        isPickup={pendingShipIsPickup}
      />

      {/* Order-wide image lightbox — arrows and arrow keys flick through every
          image on this order; closing hands the scanner back (§6.2). */}
      <ProductionImageLightbox
        open={lightbox.open}
        onOpenChange={lightbox.setOpen}
        images={lightbox.images}
        index={lightbox.index}
        onIndexChange={lightbox.setIndex}
        copy={{
          title: t('packer.preview.lightboxTitle'),
          description: t('packer.preview.lightboxDescription'),
          previousLabel: t('preview.previousImage'),
          nextLabel: t('preview.nextImage'),
        }}
      />

      {/* Scan error modal */}
      <ScanErrorModal {...feedback.modalProps} />

      {/* Superseded-sticker dialog (amber; one-tap reprint of the live replacement) */}
      <SupersededStickerDialog
        {...feedback.supersededProps}
        locationId={locationId}
        currentStage="Ready to Pack"
      />
    </div>
  );
}
